import React, { useState } from "react";
import ReportSection from "./reportSection";

const EMPTY_VALUE = "—";
const APPROVER_LEVEL_FIELDS = ["approver1_name", "approver2_name", "approver3_name"];

/** Rate and threshold unit types are stored lowercase ("daily", "hours"); the report reads better capitalised. */
const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const isBlank = (value) => value === null || value === undefined || value === "";

/**
 * MySQL DATE columns arrive either as "2025-10-01" or as a full ISO timestamp. Taking
 * the leading date part avoids the day-shift that `new Date(...)` introduces for
 * browsers east or west of UTC.
 */
const formatDate = (value) => {
  if (isBlank(value)) return EMPTY_VALUE;
  const text = String(value);
  const match = text.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : text;
};

/**
 * Drawn rather than written as an arrow character, which the report's font stack does
 * not carry and so renders through an inconsistent fallback face.
 */
const TermArrow = () => (
  <svg
    className="ces-cd-term-arrow"
    viewBox="0 0 16 8"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M0.75 4h12.5M10.25 1l3 3-3 3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ContractTerm = ({ startDate, endDate }) => {
  if (isBlank(startDate) && isBlank(endDate)) return EMPTY_VALUE;

  return (
    <span className="ces-cd-term">
      <span>{formatDate(startDate)}</span>
      <TermArrow />
      <span>{formatDate(endDate)}</span>
    </span>
  );
};

/** A position can be covered on more than one shift; the proc returns them comma separated. */
const formatShiftType = (value) => {
  if (isBlank(value)) return EMPTY_VALUE;
  return String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" \u00b7 ");
};

const formatCurrency = (value) => {
  if (isBlank(value)) return EMPTY_VALUE;
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return (
    "$ " +
    num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

/** Thresholds are DECIMAL columns, so "96.0000" needs trimming before the unit reads naturally. */
const formatThreshold = (value, unitType) => {
  if (isBlank(value)) return EMPTY_VALUE;
  const num = Number(value);
  if (isNaN(num)) return String(value);
  const amount = num.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return isBlank(unitType) ? amount : `${amount} ${unitType}`;
};

const rateUnitSuffix = (rateUnitType) => {
  const unit = (rateUnitType || "").toLowerCase();
  if (unit === "daily") return "day";
  if (unit === "hourly") return "hr";
  if (unit === "none" || unit === "") return "";
  return unit;
};

/**
 * A zero tier rate is a "no pay below this threshold" step rather than a priced one,
 * so it carries no unit — matching how the tier table reads on the contract itself.
 */
const formatTierRate = (rate, rateUnitType) => {
  const amount = formatCurrency(rate);
  if (amount === EMPTY_VALUE) return EMPTY_VALUE;
  const suffix = rateUnitSuffix(rateUnitType);
  return !suffix || Number(rate) === 0 ? amount : `${amount} / ${suffix}`;
};

/**
 * sp_select_provider_position_contract_details returns one row per contract tier,
 * with the contract level columns (including the headline `contract_rate`) repeated
 * on each row and the per-tier amount in `tier_rate`. Rows are folded back into a
 * contract per contract_mstr_key so the section can render the terms once and list
 * the tiers beneath them.
 */
const groupRowsByContract = (rows) => {
  const contracts = [];
  const byKey = new Map();

  rows.forEach((row) => {
    const key = row.contract_mstr_key ?? row.contract_name ?? "contract";
    if (!byKey.has(key)) {
      const contract = { key, ...row, tiers: [] };
      byKey.set(key, contract);
      contracts.push(contract);
    }
    byKey.get(key).tiers.push(row);
  });

  contracts.forEach((contract) => {
    contract.tiers.sort((a, b) => (a.contract_tier || 0) - (b.contract_tier || 0));
  });

  return contracts;
};

const getApprovers = (contract) =>
  APPROVER_LEVEL_FIELDS.map((field, index) => ({
    level: index + 1,
    name: contract[field],
  })).filter((approver) => !isBlank(approver.name));

const Field = ({ label, value }) => (
  <div className="ces-cd-field">
    <div className="ces-cd-label">{label}</div>
    <div className="ces-cd-value">{isBlank(value) ? EMPTY_VALUE : value}</div>
  </div>
);

const TierTable = ({ tiers, rateUnitType }) => (
  <div className="ces-cd-tier-table" data-testid="ces-cd-tier-table">
    <div className="ces-cd-tier-row ces-cd-tier-head">
      <span>Tier</span>
      <span>Min</span>
      <span>Max</span>
      <span>Rate</span>
    </div>
    {tiers.map((tier, index) => (
      <div className="ces-cd-tier-row" key={tier.contract_tier ?? index}>
        <span className="ces-cd-tier-name">Tier {tier.contract_tier ?? index + 1}</span>
        <span>{formatThreshold(tier.min_thresold, tier.thresold_unit_type)}</span>
        <span>{formatThreshold(tier.max_thresold, tier.thresold_unit_type)}</span>
        <span className="ces-cd-tier-rate">
          {formatTierRate(tier.tier_rate, tier.tier_rate_unit_type || rateUnitType)}
        </span>
      </div>
    ))}
  </div>
);

const SummaryItem = ({ label, value, emphasize = false }) => (
  <div className="ces-cd-summary-item">
    <span className="ces-cd-summary-label">{label}</span>
    <span
      className={`ces-cd-summary-value${
        emphasize ? " ces-cd-summary-link" : ""
      }`}
    >
      {isBlank(value) ? EMPTY_VALUE : value}
    </span>
  </div>
);

/**
 * A provider commonly holds several contracts at once, so each one opens from its own
 * header rather than the section listing every contract in full. The header carries
 * enough to tell the contracts apart while they are all closed.
 */
const ContractSummary = ({ contract, isOpen, onToggle }) => (
  <div
    className="ces-cd-summary"
    onClick={onToggle}
    role="button"
    tabIndex={0}
    aria-expanded={isOpen}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    }}
    data-testid="ces-cd-contract-toggle"
  >
    <SummaryItem label="Contract Type" value={contract.contract_type} />
    <SummaryItem label="Contract Name" value={contract.contract_name} emphasize />
    <SummaryItem label="Location" value={contract.location_name} />
    <SummaryItem
      label="Contract Term"
      value={
        <ContractTerm
          startDate={contract.contract_start_date}
          endDate={contract.contract_end_date}
        />
      }
    />
    <span
      className={`ces-cd-summary-chevron ${isOpen ? "expanded" : ""}`}
      aria-hidden="true"
    >
      &#9660;
    </span>
  </div>
);

const ContractDetail = ({ contract }) => {
  const approvers = getApprovers(contract);

  return (
    <div className="ces-cd-detail">
      <div className="ces-cd-subhead">Contract</div>
      <div className="ces-cd-grid">
        <Field label="Division" value={contract.division_name} />
        <Field label="Hospital / Location" value={contract.location_name} />
        <Field label="Contract Type" value={contract.contract_type} />
        <Field label="Contract Name" value={contract.contract_name} />
        <Field
          label="Contract Term"
          value={
            <ContractTerm
              startDate={contract.contract_start_date}
              endDate={contract.contract_end_date}
            />
          }
        />
        <Field label="Shift Type" value={formatShiftType(contract.shift_type_name)} />
        <Field label="Period Type" value={capitalize(contract.period_type)} />
      </div>

      <div className="ces-cd-lower">
        <div className="ces-cd-rate-structure">
          <div className="ces-cd-subhead">Rate Structure</div>
          {contract.tiers.length > 0 && (
            <TierTable
              tiers={contract.tiers}
              rateUnitType={contract.contract_rate_unit_type}
            />
          )}
        </div>

        <div className="ces-cd-approvers">
          <div className="ces-cd-subhead">Approver Details</div>
          {approvers.length === 0 ? (
            <div className="ces-cd-approver-empty">No approvers assigned</div>
          ) : (
            approvers.map((approver) => (
              <div className="ces-cd-approver-row" key={approver.level}>
                <span className="ces-cd-approver-level">Level {approver.level}</span>
                <span className="ces-cd-approver-name">{approver.name}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ContractBlock = ({ contract }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="ces-cd-contract" data-testid="ces-cd-contract">
      <ContractSummary
        contract={contract}
        isOpen={isOpen}
        onToggle={() => setIsOpen((open) => !open)}
      />
      {isOpen && <ContractDetail contract={contract} />}
    </div>
  );
};

/**
 * `rows` of null means the contract lookup has not run for the selected position yet,
 * which keeps the section out of the report entirely rather than flashing an empty card.
 */
const ContractDetailsSection = ({ rows, loading }) => {
  if (rows === null && !loading) return null;

  const contracts = groupRowsByContract(rows || []);
  const subtitle = loading
    ? "Terms, rates and approvers"
    : `${contracts.length} active contract${
        contracts.length === 1 ? "" : "s"
      } \u00b7 terms, rates and approvers`;

  return (
    <ReportSection
      title="Contractual Details"
      subtitle={subtitle}
      testId="ces-contract-details-section"
    >
      {loading && (
        <div className="ces-cd-status">Loading contract details...</div>
      )}
      {!loading && contracts.length === 0 && (
        <div className="ces-cd-status">
          No contract details found for this position
        </div>
      )}
      {!loading &&
        contracts.map((contract) => (
          <ContractBlock key={contract.key} contract={contract} />
        ))}
    </ReportSection>
  );
};

export default ContractDetailsSection;
